import React, { useState, useEffect } from 'react';
import Loan from "./loans/Loan";
import api from '../api';
import LoanDetail from './loans/LoanDetail';
import Skeleton from 'react-loading-skeleton';
import { useBrowserHistory } from '../hooks/useBrowserHistory';

const STATUS_FILTERS = [
    { code: 'active', label: 'Al día' },
    { code: 'arrear', label: 'Mora' },
    { code: 'pending', label: 'Pendiente' },
    { code: 'payed', label: 'Finalizado' },
    { code: 'cancelled', label: 'Anulado' }
];

function LoansSkeleton() {
    return (
        <div className="loans-skeleton">
            {/* Resumen Skeleton */}
            <div className="card mb-4">
                <div className="card-body p-4">
                    <Skeleton width={150} className="mb-3" />
                    <div className="row g-3">
                        <div className="col-6">
                            <div className="d-flex align-items-center">
                                <Skeleton circle width={48} height={48} className="me-3" />
                                <div className="flex-grow-1">
                                    <Skeleton width={100} />
                                    <Skeleton width={80} />
                                </div>
                            </div>
                        </div>
                        <div className="col-6">
                            <div className="d-flex align-items-center">
                                <Skeleton circle width={48} height={48} className="me-3" />
                                <div className="flex-grow-1">
                                    <Skeleton width={100} />
                                    <Skeleton width={80} />
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Préstamos Skeleton */}
            <div className="list-group list-group-flush">
                {[1, 2, 3].map((i) => (
                    <div key={i} className="card border-0 shadow-sm mb-3">
                        <div className="card-body p-4">
                            <Skeleton height={24} className="mb-3" />
                            <div className="row g-3">
                                <div className="col-4">
                                    <Skeleton height={20} />
                                </div>
                                <div className="col-4">
                                    <Skeleton height={20} />
                                </div>
                                <div className="col-4">
                                    <Skeleton height={20} />
                                </div>
                            </div>
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
}

function LoansContainer({ token, client }) {
    const [loans, setLoans] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [page, setPage] = useState(1);
    const [hasMore, setHasMore] = useState(false);
    const [activeFilters, setActiveFilters] = useState([]);
    const [selectedLoanId, setSelectedLoanId] = useState(null);
    const [installmentsByLoanId, setInstallmentsByLoanId] = useState({});

    const fetchLoans = async (pageNum = 1, filters = activeFilters) => {
        // Development mode: return empty loans
        if (process.env.REACT_APP_DEV_MODE === 'true') {
            setLoans({ loans: [], pagination: { current_page: 1, total_pages: 1, total: 0 } });
            setHasMore(false);
            setPage(1);
            setLoading(false);
            setError(null);
            return;
        }
        
        try {
            const response = await api.get(`/c/${token}/l`, {
                headers: { dni: localStorage.getItem(token + 'dni') },
                params: {
                    page: pageNum,
                    status: filters.join(',')
                }
            });
            
            if (response.data && response.data.data) {
                if (pageNum === 1) {
                    setLoans(response.data.data);
                } else {
                    setLoans(prevLoans => ({
                        ...prevLoans,
                        loans: [...prevLoans.loans, ...response.data.data.loans]
                    }));
                }

                const pagination = response.data.data.pagination;
                if (pagination) {
                    setHasMore(pagination.current_page < pagination.total_pages);
                    setPage(pageNum);
                }
                
                setError(null);
            } else {
                throw new Error('Formato de respuesta inválido');
            }
        } catch (error) {
            console.error('Error al cargar préstamos:', error);
            setError('No se pudieron cargar los préstamos');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchLoans();
    }, [token]); // Solo se ejecuta cuando se monta el componente o cambia el token

    // Fetch installments for each loan (for progress bar). Skip in dev mode or if progress bar is disabled.
    useEffect(() => {
        if (!loans?.loans?.length || 
            process.env.REACT_APP_DEV_MODE === 'true' || 
            !client?.configuration?.show_progress_bar) {
            return;
        }

        let cancelled = false;
        const fetchAllInstallments = async () => {
            const loansNeedingInstallments = loans.loans.filter(
                (loan) => !['PAYMENT_PLAN_ACTIVE', 'PAYMENT_PLAN_DELINQUENT'].includes(loan.display_mode)
            );

            if (!loansNeedingInstallments.length) {
                return;
            }

            const results = await Promise.allSettled(
                loansNeedingInstallments.map((loan) =>
                    api.get(`/c/${token}/l/${loan.uid}/i`, {
                        headers: { dni: localStorage.getItem(token + 'dni') }
                    }).then((res) => ({ uid: loan.uid, installments: res.data?.data?.installments ?? null }))
                )
            );
            if (cancelled) return;
            const next = {};
            results.forEach((r, i) => {
                const uid = loansNeedingInstallments[i].uid;
                next[uid] = r.status === 'fulfilled' ? r.value.installments : null;
            });
            setInstallmentsByLoanId((prev) => ({ ...prev, ...next }));
        };
        fetchAllInstallments();
        return () => { cancelled = true; };
    }, [loans?.loans, token, client?.configuration?.show_progress_bar]);

    const handleLoadMore = () => {
        fetchLoans(page + 1);
    };

    const handleFilterChange = (newFilters) => {
        setActiveFilters(newFilters);
        setPage(1);
        fetchLoans(1, newFilters);
    };

    const handleLoanClick = (loanUid) => {
        // Ensure 'loans' state is in history before adding detail state
        if (!window.history.state || window.history.state.reactState !== 'loans') {
            window.history.pushState(
                { reactState: 'loans' },
                '',
                window.location.href
            );
        }
        // Add history entry when navigating to detail
        window.history.pushState(
            { reactState: `loan-detail-${loanUid}` },
            '',
            window.location.href
        );
        setSelectedLoanId(loanUid);
    };

    const handleBack = () => {
        // Go back in history when clicking back button
        window.history.back();
    };

    // Handle browser back/forward navigation for loan detail
    const handleBrowserStateChange = (newState) => {
        // If state is 'loans', we're back to the list (no detail selected)
        if (newState === 'loans') {
            setSelectedLoanId(null);
        } 
        // If state is a loan detail, navigate to that detail
        else if (newState.startsWith('loan-detail-')) {
            const loanId = newState.replace('loan-detail-', '');
            setSelectedLoanId(loanId);
        }
        // If state is something else (like 'home' or 'payments'), clear selection
        // This will be handled by Client.jsx to change tabs
        else {
            setSelectedLoanId(null);
        }
    };

    // Handle popstate when in detail view to go back to list
    useEffect(() => {
        if (!selectedLoanId) return;

        const handlePopState = (event) => {
            // If we're in detail and going back, check if we should go to list
            if (event.state && event.state.reactState === 'loans') {
                setSelectedLoanId(null);
            } else if (!event.state || !event.state.reactState || !event.state.reactState.startsWith('loan-detail-')) {
                // If state is not a loan detail, go back to list
                setSelectedLoanId(null);
            }
        };

        window.addEventListener('popstate', handlePopState);
        return () => {
            window.removeEventListener('popstate', handlePopState);
        };
    }, [selectedLoanId]);

    // Handle browser back when in loans list - go to home
    useEffect(() => {
        if (selectedLoanId) return; // Only handle when in list view

        const handlePopState = (event) => {
            // If we're in loans list and there's no state or state is 'loans', go to home
            if (!event.state || !event.state.reactState || event.state.reactState === 'loans') {
                // Trigger navigation to home via window event
                window.dispatchEvent(new CustomEvent('navigate-to-home'));
                // Push state back to prevent navigation
                window.history.pushState({ reactState: 'loans' }, '', window.location.href);
            }
        };

        window.addEventListener('popstate', handlePopState);
        return () => {
            window.removeEventListener('popstate', handlePopState);
        };
    }, [selectedLoanId]);

    // Track history when viewing a loan detail
    // When viewing the list, we handle back manually to go to home
    const currentHistoryState = selectedLoanId ? `loan-detail-${selectedLoanId}` : null;
    useBrowserHistory(currentHistoryState, handleBrowserStateChange, !!selectedLoanId);

    if (loading) {
        return <LoansSkeleton />;
    }

    if (error) {
        return (
            <div className="text-center p-4">
                <div className="text-danger mb-3">
                    <i className="bi bi-exclamation-circle display-4"></i>
                </div>
                <h5>{error}</h5>
                <button 
                    className="btn btn-outline-primary mt-3"
                    onClick={() => fetchLoans()}
                >
                    Reintentar
                </button>
            </div>
        );
    }

    if (!loans || !loans.loans) return null;

    const formatCurrency = (amount) => {
        return new Intl.NumberFormat('es-AR', {
            style: 'currency',
            currency: 'ARS'
        }).format(amount);
    };

    const renderSummary = () => {
        return (
            <div className="d-flex align-items-center mb-4">
                <h5 className="mb-0 mt-1">Mis Préstamos</h5>
            </div>
        );


        if (!loans.summary) return null;

        return (
            <div className="card mb-4">
                <div className="card-body p-4">
                    <h6 className="text-muted mb-3">Resumen de Cuenta</h6>
                    <div className="row g-3">
                        <div className="col-6">
                            <div className="d-flex align-items-center">
                                <div className="icon-circle  bg-warning-subtle">
                                    <i className="bi bi-wallet2 text-warning icon-md"></i>
                                </div>
                                <div className="ms-3">
                                    <span className="d-block text-muted small">Deuda Total</span>
                                    <h6 className="mb-0 text-danger">
                                        {formatCurrency(loans.summary.total_debt)}
                                    </h6>
                                </div>
                            </div>
                        </div>
                        <div className="col-6">
                            <div className="d-flex align-items-center">
                                <div className="icon-circle bg-danger-subtle">
                                    <i className="bi bi-calendar-x text-danger icon-md"></i>
                                </div>
                                <div className="ms-3">
                                    <span className="d-block text-muted small">Vencido</span>
                                    <h6 className="mb-0 text-danger">
                                        {formatCurrency(loans.summary.overdue_amount)}
                                    </h6>
                                </div>
                            </div>
                        </div>
                        <div className="col-12">
                            <hr className="my-2" />
                            <div className="d-flex justify-content-between align-items-center">
                                <span className="text-muted small">Saldo Pendiente</span>
                                <h6 className="mb-0">
                                    {formatCurrency(loans.summary.outstanding_balance)}
                                </h6>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        );
    };

    if (selectedLoanId) {
        return <LoanDetail loanId={selectedLoanId} token={token} onBack={handleBack} client={client} />;
    }

    if (loans.loans.length === 0) {
        return (
            <div className="card">
                <div className="card-body text-center p-4">
                    <div className="avatar avatar-lg bg-light-subtle mb-3">
                        <i className="bi bi-inbox text-secondary"></i>
                    </div>
                    <h6 className="text-muted">No hay préstamos disponibles</h6>
                </div>
            </div>
        );
    }

    return (
        <div className="loans-container pb-5">
            {renderSummary()}

            {/* Filters Section por ahora lo dejamos oculto, es para implemetar el filtro de estado */}
            {/* <div className="justify-content-between w-100 mb-4 d-flex gap-2 flex-wrap p-2 pt-0">
                {STATUS_FILTERS.map(filter => (
                    <button
                        key={filter.code}
                        className={`btn btn-sm ${
                            activeFilters.includes(filter.code)
                                ? 'btn-primary'
                                : 'btn-outline-secondary'
                        }`}
                        onClick={() => handleFilterClick(filter.code)}
                    >
                        {filter.label}
                    </button>
                ))}
            </div> */}

            {/* Loans List */}
            <div className="list-group list-group-flush">
                {loans.loans.map((loan, index) => (
                    <Loan 
                        key={loan.uid} 
                        handleLoanClick={handleLoanClick}
                        data={loan} 
                        token={token}
                        installments={installmentsByLoanId[loan.uid]}
                        client={client}
                        className={index === loans.loans.length - 1 ? '' : 'mb-3'}
                    />
                ))}
            </div>
            
            {hasMore && (
                <div className="text-center mt-4">
                    <button 
                        className="btn btn-outline-primary"
                        onClick={handleLoadMore}
                    >
                        Ver más
                    </button>
                </div>
            )}
        </div>
    );
}

export default LoansContainer;